import { useEffect, useState } from "react";
import {
  Alert, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Icon, IconButton, InputLabel, Switch,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { coresDoTema } from "assets/coresTema";
import { errorAlert, successAlert } from "utils/Functions";
import { get, post } from "utils/WebService";
import { corDaFuncao } from "./iaUtils";

const rotulo = { px: 1, pb: 0.5, pt: 0.2 };
const paraTexto = (v) => (v === null || v === undefined ? "" : String(v).replace(".", ","));

/**
 * Limites e controles da IA (Ferramentas > Uso da IA; gravar exige a opção Editar, c088_U): cada função ligada ou
 * desligada, o limite por usuário por dia (em branco = sem limite), o teto de gasto do mês para o sistema todo (ao bater,
 * a IA para até o mês virar), a cotação do dólar (para mostrar em reais) e os preços por milhão de tokens (o custo
 * estimado; o real vem da OpenAI). Backend: GET/POST /ia-uso/controles.
 */
export default function ControlesIa({ open, onClose, onSalvo }) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const cores = coresDoTema(darkMode);
  const [dados, setDados] = useState(null);
  const [form, setForm] = useState(null);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDados(null);
    get("/ia-uso/controles")
      .then((res) => {
        setDados(res);
        setForm({
          funcoes: (res.funcoes || []).map((f) => ({ ...f, limiteDia: f.limiteDia === null || f.limiteDia === undefined ? "" : String(f.limiteDia) })),
          teto: paraTexto(res.teto),
          cotacao: paraTexto(res.cotacao),
          precoEntrada: paraTexto(res.precoEntrada),
          precoCache: paraTexto(res.precoCache),
          precoSaida: paraTexto(res.precoSaida),
        });
      })
      .catch((e) => {
        errorAlert(e?.message || "Erro ao carregar os controles da IA.");
        onClose();
      });
  }, [open]);

  const pode = !!dados?.podeAlterar;
  const mudarFuncao = (i, campo, valor) => setForm((f) => ({ ...f, funcoes: f.funcoes.map((x, j) => (j === i ? { ...x, [campo]: valor } : x)) }));
  const campo = (chave) => (e) => setForm({ ...form, [chave]: e.target.value });

  const salvar = () => {
    setSalvando(true);
    post("/ia-uso/controles", {
      ...form,
      funcoes: form.funcoes.map((f) => ({ codigo: f.codigo, ativa: f.ativa, limiteDia: String(f.limiteDia || "").trim() })),
    })
      .then(() => {
        successAlert("Limites e controles da IA salvos. Valem a partir do próximo pedido.");
        onSalvo && onSalvo();
        onClose();
      })
      .catch((e) => errorAlert(e?.message || "Erro ao salvar os controles da IA."))
      .finally(() => setSalvando(false));
  };

  const ajuda = (texto) => (
    <MDTypography variant="caption" color="text" display="block" sx={{ px: 1, mt: 0.5 }}>{texto}</MDTypography>
  );
  const cab = { fontWeight: "bold", fontSize: "0.72rem", backgroundColor: cores.painel, color: cores.texto, py: 1 };
  const cel = { fontSize: "0.8rem", color: cores.texto, verticalAlign: "top", pt: 1.5 };

  return (
    <Dialog open={open} onClose={() => !salvando && onClose()} maxWidth="md" fullWidth PaperProps={{ sx: { background: cores.cartao } }}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
        <MDTypography variant="h6" color="text">Limites e controles da IA</MDTypography>
        <IconButton size="small" onClick={onClose} disabled={salvando}><Icon>close</Icon></IconButton>
      </DialogTitle>
      <DialogContent>
        {!form ? (
          <MDBox display="flex" justifyContent="center" py={4}><CircularProgress size={28} color="info" /></MDBox>
        ) : (
          <>
            {!pode && (
              <Alert severity="info" sx={{ mb: 2, fontSize: "0.85rem" }}>
                Só leitura: alterar exige o Uso da IA com a opção Editar no perfil (Perfis &gt; Uso da IA &gt; Editar).
              </Alert>
            )}
            <TableContainer sx={{ boxShadow: "none", border: `1px solid ${cores.divisoria}`, borderRadius: 1, mb: 2 }}>
              <Table size="small">
                <TableHead sx={{ display: "table-header-group" }}>
                  <TableRow>
                    <TableCell sx={cab}>Função</TableCell>
                    <TableCell sx={cab} align="center">Ligada</TableCell>
                    <TableCell sx={{ ...cab, width: 190 }}>Limite por usuário por dia</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {form.funcoes.map((f, i) => (
                    <TableRow key={f.codigo}>
                      <TableCell sx={cel}>
                        <MDBox display="flex" alignItems="center" gap={1}>
                          <MDBox sx={{ width: 10, height: 10, minWidth: 10, borderRadius: "50%", background: corDaFuncao(f.codigo, darkMode) }} />
                          <MDTypography variant="button" fontWeight="medium" sx={{ color: cores.texto }}>{f.nome}</MDTypography>
                        </MDBox>
                        <MDTypography variant="caption" display="block" sx={{ color: cores.textoSecundario, pl: 2.25 }}>{f.onde}</MDTypography>
                      </TableCell>
                      <TableCell sx={{ ...cel, pt: 1 }} align="center">
                        <Switch checked={!!f.ativa} disabled={!pode} onChange={(e) => mudarFuncao(i, "ativa", e.target.checked)} />
                      </TableCell>
                      <TableCell sx={cel}>
                        <MDInput size="small" fullWidth type="number" inputProps={{ min: 1 }} value={f.limiteDia} disabled={!pode}
                          placeholder="Sem limite" onChange={(e) => mudarFuncao(i, "limiteDia", e.target.value)} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {ajuda("Desligada: o botão da função continua na tela, mas o pedido é recusado com o aviso. O limite conta os pedidos atendidos de cada usuário no dia (os resumos reaproveitados, os com erro e os descartados não contam); em branco = sem limite.")}
            <Grid container spacing={2} mt={0.5}>
              <Grid item xs={12} md={6}>
                <InputLabel sx={rotulo}>Teto de gasto do mês (US$)</InputLabel>
                <MDInput size="small" fullWidth value={form.teto} disabled={!pode} placeholder="Sem teto" onChange={campo("teto")} />
                {ajuda("Para o sistema todo. Ao chegar nele, nenhuma função usa a IA até o dia 1º do mês seguinte (ou até aumentar o teto). Vale o maior entre o custo real da OpenAI e o estimado pelo sistema.")}
              </Grid>
              <Grid item xs={12} md={6}>
                <InputLabel sx={rotulo}>Cotação do dólar (R$)</InputLabel>
                <MDInput size="small" fullWidth value={form.cotacao} disabled={!pode} placeholder="Só em dólar" onChange={campo("cotacao")} />
                {ajuda("Só para mostrar os valores também em reais. A OpenAI cobra em dólar.")}
              </Grid>
              <Grid item xs={12}>
                <MDTypography variant="button" fontWeight="bold" color="text" display="block" sx={{ px: 1 }}>
                  Preço por milhão de tokens (US$)
                </MDTypography>
                {ajuda("Dá o custo estimado de cada pedido (os tokens vêm da própria OpenAI). Com a chave de administrador, o custo real da OpenAI corrige a estimativa. Padrão: o preço do gpt-4o-mini (0,15 entrada, 0,075 entrada em cache, 0,60 saída).")}
              </Grid>
              <Grid item xs={12} md={4}>
                <InputLabel sx={rotulo}>Entrada</InputLabel>
                <MDInput size="small" fullWidth value={form.precoEntrada} disabled={!pode} onChange={campo("precoEntrada")} />
              </Grid>
              <Grid item xs={12} md={4}>
                <InputLabel sx={rotulo}>Entrada em cache</InputLabel>
                <MDInput size="small" fullWidth value={form.precoCache} disabled={!pode} onChange={campo("precoCache")} />
              </Grid>
              <Grid item xs={12} md={4}>
                <InputLabel sx={rotulo}>Saída</InputLabel>
                <MDInput size="small" fullWidth value={form.precoSaida} disabled={!pode} onChange={campo("precoSaida")} />
              </Grid>
            </Grid>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <MDButton variant="outlined" color="secondary" size="medium" onClick={onClose} disabled={salvando}>Fechar</MDButton>
        {pode && (
          <MDButton variant="contained" color="info" size="medium" onClick={salvar} disabled={salvando || !form}>
            {salvando ? <CircularProgress size={14} color="inherit" sx={{ mr: 0.75 }} /> : <Icon sx={{ mr: 0.5 }}>save</Icon>}
            {salvando ? "Salvando..." : "Salvar"}
          </MDButton>
        )}
      </DialogActions>
    </Dialog>
  );
}
